/*
 *
 * File: FixPriceRoutesScreen.js
 *
 * Purpose: Fix price routes (Festpreisstrecken) component file.
 *
 * Author:   Thomas Burgard (Software development)
 * Created:  20.03.2023
 * Modified: 17.05.2023
 *
 * Modifications:
 *     - New file for first implementation (20.03.2023)
 *     - Stripe credit card payment inserted (17.05.2023)
 *
 * Copyright (c) DrivePartner GmbH 2023
 *
 */

import { React, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { addLocale } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { ListBox } from "primereact/listbox";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";
import {
	timeConvert,
	getCarCategoryData,
	getTariffs,
	getUserRegistrationData,
	roundFare,
} from "../utils/utils";
import * as constants from "../utils/constants";
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";
import ChooseCarCategoryOfferFixPrice from "../components/chooseoffer/ChooseCarCategoryOfferFixPrice";
import { bookingDataValuesObj } from "../utils/dataObjects";
import { SEND_BOOKINGDATA } from "../api/actionCodes";
import { CREATE_PAYMENT_INTENT } from "../api/actionCodes";

//--------------------------------------------------------------
//--- CSS styles -----------------------------------------------
//--------------------------------------------------------------
const cardElementOptions = {
	style: {
		base: {
			iconColor: "#3f51b5",
			color: "#3f51b5",
			//fontWeight: "500",
			//fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
			fontSize: "16px",
			fontSmoothing: "antialiased",
			":-webkit-autofill": {
				color: "#fce883",
			},
			"::placeholder": {
				color: "#afb5dc",
			},
		},
		invalid: {
			iconColor: "#c52b2b",
			color: "#c52b2b",
		},
	},
};
//--- End Css styles -------------------------------------------

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
const myCustomEuropeBounds = new window.google.maps.LatLngBounds(
	new window.google.maps.LatLng(34.4474235427, -24.1252624989),
	new window.google.maps.LatLng(71.1858787729, 35.0251281261)
);
const options = { bounds: myCustomEuropeBounds, strictBounds: true };
const bookingDataFormularValuesObj = bookingDataValuesObj;
const waypoints = [];
const minDate = moment().toDate();
const regEmail =
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const fieldsErrorState = {
	errorPickupFrom: true,
	errorTransferTo: true,
	errorPickupTime: true,
	errorPickupDate: false,
	errorPassengerName: true,
	errorBookedFrom: true,
	errorPhone: true,
	errorEmail: true,
	errorTerms: true,
};

// Server URL for React API
const URL_SERV = "https://api.drivepartner.com/multistop/";
const PHP_FILE_STOREFIXPRICEBOOKINGDATA = "store_fixpriceroutebookingdata.php";
const PHP_FILE_CREATE_PAYMENTINTENT = "stripe_paymentintent.php";

let directionsRenderer = new window.google.maps.DirectionsRenderer();
let directionsService = new window.google.maps.DirectionsService();
let startAddress = "";
let endAddress = "";
let addCounter = 0;
let useEffectFirstCounter = 0;
let timeDateChanged = false;
let firstID = uuidv4();
let uuid = "";
let removeBtnClicked = false;
let startPosition = null;
let waypoint1 = "";
let waypoint2 = "";
let waypoint3 = "";
let endPosition = null;
let startEndPointDistance = null;
let startEndPointDrivingTime = null;
let userCalcRouteObj = null;
let userCarCategoryObj = null;
let userRegistrationDataObj = null;
let calcFaresFinished = false;
//let disableAddBtn = false;
let acceptancePeriodMissed = false;
let emailError = false;
let googleResultHasPostCodeFrom = false;
let googleResultHasPostCodeTo = false;
let pickupFromPostcode = "-";
let pickupToPostcode = "-";
let freeKilometers = null;
let startPrice = null;
let calcPrice = null;
let carSelected = false;
let tmpWaypoint1 = "";
let tmpWaypoint2 = "";
let tmpWaypoint3 = "";
//---End component variable declarations -------------------------

/**
 * Implementation of DrivePartner fix price routes (Festpreisstrecken).
 *
 * Used Google-APIs:
 *   - Google Autocomplete
 *   - Google Places
 *
 * @param {*} props
 * @returns JSX-HTML-DOM
 */
const FixPriceRoutesScreen = (props) => {
	const userData = props.userData;
	const whitelabel = props.userData.regData.WHITELABEL;
	const { t, i18n } = useTranslation("common");
	const stripe = useStripe();
	const elements = useElements();

	//--------------------------------------------------------------
	//--- REDUX ----------------------------------------------------
	//--------------------------------------------------------------
	const selectedLanguage = useSelector((state) => state.languageReducer);

	//--------------------------------------------------------------
	//--- Navigation -----------------------------------------------
	//--------------------------------------------------------------
	const navigate = useNavigate();

	//--------------------------------------------------------------
	//--- Refs -----------------------------------------------------
	//--------------------------------------------------------------
	const choosenCarCategory = useRef();

	//--------------------------------------------------------------
	//--- Component states -----------------------------------------
	//--------------------------------------------------------------
	const [ingredient, setIngredient] = useState(
		constants.PAYMENT_METHOD_ON_DRIVER
	);
	const [cardPaymentEnabled, setCardPaymentEnabled] = useState(false);
	const [disableAddBtn, setDisableAddBtn] = useState(true);
	const [errorBackgroundPickupFrom, setErrorBackgroundPickupFrom] =
		useState(true);
	const [errorBackgroundTransferTo, setErrorBackgroundTransferTo] =
		useState(true);
	//const [errorBackgroundDate, setErrorBackgroundDate] = useState(false);
	const [errorBackgroundDate, setErrorBackgroundDate] = useState(true);
	const [errorBackgroundTime, setErrorBackgroundTime] = useState(true);
	const [errorBackgroundPassengerName, setErrorBackgroundPassengerName] =
		useState(true);
	const [errorBackgroundBookedFrom, setErrorBackgroundBookedFrom] =
		useState(true);
	const [errorBackgroundPhone, setErrorBackgroundPhone] = useState(true);
	const [errorBackgroundEmail, setErrorBackgroundEmail] = useState(true);
	const [errorBackgroundTerms, setErrorBackgroundTerms] = useState(true);
	const [termsOfUseChecked, setTermsOfUseChecked] = useState(false);
	const [formValues, setFormValues] = useState([{ name: "", id: firstID }]);
	const [paxValue, setPaxValue] = useState(1);
	const [tipValue, setTipValue] = useState(0);
	const [routingResultValues, setRoutingResultValues] = useState(null);
	const [selectedPayment, setSelectedPayment] = useState(
		constants.PAYMENT_CREDIT_CARD
	);
	const [showErrorInfoBox, setShowErrorInfoBox] = useState("infobox_empty");
	const [routeSelected, setRouteSelected] = useState(false);
	const [validation, setValidation] = useState({
		pickupfrom: null,
		transferto: null,
		pickupTime: null,
		//pickupDate: moment().toDate(),
		pickupDate: null,
		passengerName: null,
		bookedFrom: null,
		phone: null,
		email: null,
	});
	const [selectedFixRoute, setSelectedFixRoute] = useState(null);

	const carCategoryAlternativeName = useRef("");
	//--- End states -----------------------------------------------

	const payment = [
		{ value: "Credit card", label: t("formular.dropdown-payment-credit-card") },
		{
			value: "On Company Bill",
			label: t("formular.dropdown-payment-on-company-bill"),
		},
		{ value: "Cash", label: t("formular.dropdown-payment-cash") },
	];

	// Set calendar (date picker) to english language and first day is monday (0 = sunday, 1 = monday)!
	addLocale("en", { firstDayOfWeek: 1 });

	userRegistrationDataObj = getUserRegistrationData(userData);
	userCalcRouteObj = getTariffs(userData);

	//--------------------------------------------------------------
	//--- Will be called after "first" rendering! ------------------
	//--------------------------------------------------------------
	useEffect(() => {
		if (useEffectFirstCounter > 0) {
			//window.location.reload(false);
			resetRoute();
		}

		const autocompleteStart = new window.google.maps.places.Autocomplete(
			document.getElementById(firstID),
			options
		);
		const autocompleteEnd = new window.google.maps.places.Autocomplete(
			document.getElementById("transferto"),
			options
		);

		//--------------------------------------------------------------
		//--- Google Autocomplete change listener ----------------------
		//--------------------------------------------------------------
		window.google.maps.event.addListener(
			autocompleteStart,
			"place_changed",
			() => startPositionGoogleInputListener(autocompleteStart)
		);

		//--------------------------------------------------------------
		//--- Google Autocomplete change listener ----------------------
		//--------------------------------------------------------------
		window.google.maps.event.addListener(autocompleteEnd, "place_changed", () =>
			endPositionGoogleInputListener(autocompleteEnd)
		);

		useEffectFirstCounter++;
	}, []);

	//--------------------------------------------------------------
	//--- Will be called only for "showErrorInfoBox" ---------------
	//--------------------------------------------------------------
	useEffect(() => {
		if (showErrorInfoBox !== "infobox_empty") {
			window.scrollTo(0, 2000);
		} else {
			window.scrollTo(0, 0);
		}
	}, [showErrorInfoBox]);

	//--------------------------------------------------------------
	//--- Will be called after second rendering, but not         ---
	//--- for "showErrorInfoBox"!                                ---
	//--------------------------------------------------------------
	useEffect(() => {
		if (timeDateChanged) {
			timeDateChanged = false;
		} else {
			if (addCounter > 0 && !removeBtnClicked) {
				const waypointPosition = new window.google.maps.places.Autocomplete(
					document.getElementById(uuid),
					options
				);

				if (addCounter > 0) {
					if (waypoints.length === 0) {
						waypoints.push({
							location: "", // Will be set later in function  "addListener"!
							stopover: true,
						});
					} else {
						if (waypoints[waypoints.length - 1]["location"] !== "") {
							waypoints.push({
								location: "", // Will be set later in function  "addListener"!
								stopover: true,
							});
						}
					}
				}

				if (addCounter === 1) {
					addListener(waypointPosition, uuid, 1); // First waypoint
				} else if (addCounter === 2) {
					addListener(waypointPosition, uuid, 2); // Second waypoint
				} else if (addCounter === 3) {
					addListener(waypointPosition, uuid, 3); // Third waypoint
				}
			} else if (removeBtnClicked) {
				setDisableAddBtn(false);
				removeBtnClicked = false;
			}
		}
	});

	/**
	 * Google start position field input listener.
	 *
	 * @param {*} autocompleteStart Google places autocomplete start position field
	 */
	const startPositionGoogleInputListener = (autocompleteStart) => {
		const place = autocompleteStart.getPlace();
		const latitudeSource = place.geometry.location.lat();
		const longitudeSource = place.geometry.location.lng();
		googleResultHasPostCodeFrom = false;
		pickupFromPostcode = "-";
		startPosition = new window.google.maps.LatLng(
			latitudeSource,
			longitudeSource
		);

		// Get each component of the address from the place details,
		// and then fill-in the corresponding field on the form.
		for (let idx = 0; idx < place.address_components.length; idx++) {
			const addressType = place.address_components[idx].types[0];

			if (addressType === "postal_code") {
				pickupFromPostcode = place.address_components[idx]["short_name"];
				googleResultHasPostCodeFrom = true;
			}
		}

		if (!userData.postCodes) {
			setErrorBackgroundPickupFrom(false);
			setDisableAddBtn(false);
		} else if (userData.postCodes && googleResultHasPostCodeFrom) {
			setErrorBackgroundPickupFrom(false);
			setDisableAddBtn(false);
		} else {
			setShowErrorInfoBox("infobox_error_postcode");
			setTimeout(function () {
				setShowErrorInfoBox("infobox_empty");
			}, 6000);

			resetRoute();
			//resetRouteFrom();
		}
	};

	/**
	 * Google end position field input listener.
	 *
	 * @param {*} autocompleteEnd Google places autocomplete end position field
	 */
	const endPositionGoogleInputListener = (autocompleteEnd) => {
		const place = autocompleteEnd.getPlace();
		const latitudeSource = place.geometry.location.lat();
		const longitudeSource = place.geometry.location.lng();
		googleResultHasPostCodeTo = false;
		pickupToPostcode = "-";
		endPosition = new window.google.maps.LatLng(
			latitudeSource,
			longitudeSource
		);

		// Get each component of the address from the place details,
		// and then fill-in the corresponding field on the form.
		for (let idx = 0; idx < place.address_components.length; idx++) {
			const addressType = place.address_components[idx].types[0];

			if (addressType === "postal_code") {
				pickupToPostcode = place.address_components[idx]["short_name"];
				googleResultHasPostCodeTo = true;
			}
		}

		if (!userData.postCodes) {
			setErrorBackgroundTransferTo(false);
			setDisableAddBtn(false);
		} else if (userData.postCodes && googleResultHasPostCodeTo) {
			setErrorBackgroundTransferTo(false);
			setDisableAddBtn(false);
		} else {
			setShowErrorInfoBox("infobox_error_postcode");

			setTimeout(function () {
				setShowErrorInfoBox("infobox_empty");
			}, 6000);

			resetRoute();
			//resetRouteTransferTo();
		}
	};

	/**
	 * Add Google Autocomplete change listener for route start input field
	 *
	 * @param {*} waypointPosition position of added waypoint
	 * @param {*} uuid given generated uuid for dynamically input field id
	 * @param {*} waypointNumber number (position) of the waypoint in waypoint array
	 */
	const addListener = (waypointPosition, uuid, waypointNumber) => {
		window.google.maps.event.addListener(
			waypointPosition,
			"place_changed",
			() => {
				const place = waypointPosition.getPlace();
				const latitudeSource = place.geometry.location.lat();
				const longitudeSource = place.geometry.location.lng();
				const waypoint = new window.google.maps.LatLng(
					latitudeSource,
					longitudeSource
				);

				waypoints[waypointNumber - 1]["location"] = waypoint;

				if (waypoints.length > waypointNumber) {
					waypoints.pop();
				}

				setDisableAddBtn(false);
			}
		);
	};

	//***********************************************************/
	//* Handle click event of the Add button to add waypoint    */
	//***********************************************************/
	const addWaypoint = () => {
		if (addCounter < 3) {
			addCounter++;
			removeBtnClicked = false;
			uuid = uuidv4();
			//disableAddBtn = true;
			setFormValues([...formValues, { name: "", id: uuid }]);
		}
	};

	//--------------------------------------------------------------
	//--- Handle click event of the Remove button to remove waypoint
	//--------------------------------------------------------------
	const removeWaypoint = (i) => {
		addCounter--;
		removeBtnClicked = true;
		waypoints.splice(i - 1, 1);
		let newFormValues = [...formValues];
		newFormValues.splice(i, 1);

		if (addCounter === 0) {
			//disableAddBtn = true;
			setDisableAddBtn(false);
		}

		setFormValues(newFormValues);
	};

	/**
	 * Store the choosen car category (function will
	 * be called from component "ChooseOffer").
	 *
	 * @param {*} price fix price of selected car category
	 * @param {*} selectedCarCategory Choosen car category (e.g. Business Van)
	 * @param {*} carCategoryAltName Choosen car categoryalternative name
	 */
	const choosenCarOffer = (price, selectedCarCategory, carCategoryAltName) => {
		choosenCarCategory.current = selectedCarCategory;
		carCategoryAlternativeName.current = String(carCategoryAltName);
		calcPrice = price;
		carSelected = true;
	};

	/**
	 * Set input field name of pickupfrom in case of first index,
	 * otherwise set name from element.
	 *
	 * @param {*} element waypoint element
	 * @param {*} index input field array index
	 * @returns strName name of
	 */
	const nameRouteInputField = (element, index) => {
		let strName = null;

		if (index === 0) {
			strName = "pickupfrom";
		} else {
			strName = element.id;
		}

		return strName;
	};

	/**
	 * Pickup date change handler.
	 *
	 * @param {*} event Date picker selection event
	 */
	const changeDateHandler = (event) => {
		if (validation.pickupTime !== null) {
			checkAcceptancePeriod(event, false);
		}

		timeDateChanged = true;
		const tmpValidation = validation;
		setValidation({ ...tmpValidation, pickupDate: event.value });
	};

	/**
	 * Pickup time change handler.
	 *
	 * @param {*} event Time picker selection event
	 */
	const changeTimeHandler = (event) => {
		if (validation.pickupDate !== null) {
			checkAcceptancePeriod(event, true);
		}

		timeDateChanged = true;

		const tmpValidation = validation;
		setValidation({ ...tmpValidation, pickupTime: event.value });
	};

	/**
	 * Check "Annahmefrist" of user with input date & time.
	 *
	 * @param {*} event Date picker or time picker selection event
	 * @param {*} eventIsTime Is event coming from time (true) or date (false) picker selection event
	 */
	const checkAcceptancePeriod = (event, eventIsTime) => {
		let diffPeriod = null;
		let selectedOnlyDateStr = null;
		let selectedOnlyTimeStr = null;
		let actualDateStr = null;
		let actualTime = null;
		let selectedTime = null;
		let acceptancePeriod = userRegistrationDataObj.annahmefrist;

		if (
			acceptancePeriod === "0h" ||
			acceptancePeriod === null ||
			acceptancePeriod === ""
		) {
			acceptancePeriodMissed = false;
		} else {
			acceptancePeriod = acceptancePeriod.charAt(0);
			acceptancePeriod = parseInt(acceptancePeriod) * 3600 * 1000;

			const actualElapsedTimeMillisec = moment().valueOf();

			if (eventIsTime) {
				selectedOnlyDateStr = moment(validation.pickupDate)
					.format("DD/MM/YYYY")
					.toString();
				selectedOnlyTimeStr = moment(event.value).format("HH:mm").toString(); // HH: 24 Std. format, hh: 12 Std. format
				selectedTime = moment(event.value).format("HH:mm");
			} else {
				selectedOnlyDateStr = moment(event.value)
					.format("DD/MM/YYYY")
					.toString();
				selectedOnlyTimeStr = moment(validation.pickupTime)
					.format("HH:mm")
					.toString(); // HH: 24 Std. format, hh: 12 Std. format
				selectedTime = moment(validation.pickupTime).format("HH:mm");
			}

			const newDateStr = moment(
				selectedOnlyDateStr + " " + selectedOnlyTimeStr,
				"DD/MM/YYYY HH:mm"
			).valueOf();
			diffPeriod = Math.abs(newDateStr - actualElapsedTimeMillisec);

			if (diffPeriod >= acceptancePeriod) {
				acceptancePeriodMissed = false;
			} else {
				acceptancePeriodMissed = true;
			}

			// Check if time for today date is in the past
			actualDateStr = moment().format("DD/MM/YYYY").toString();

			if (selectedOnlyDateStr === actualDateStr) {
				actualTime = moment().format("HH:mm"); // Get actual time in HH:mm format

				if (selectedTime <= actualTime) {
					// Time is in the past!
					acceptancePeriodMissed = true;
				}
			}
		}
	};

	/**
	 * Payment drop down payment selection event handler.
	 * Set selected payment in state.
	 *
	 * @param {*} e event
	 */
	const selectPaymentHandler = (e) => {
		setSelectedPayment(e.value);
	};

	/**
	 * Input change handler.
	 *
	 * @param {*} e event
	 */
	const inputChangeHandler = (e) => {
		var inputValue = e.target.value;
		const nameField = e.target.name;

		emailError = false;

		if (nameField === "email") {
			if (!regEmail.test(inputValue)) emailError = true;
		}

		const tmpValidation = validation;
		setValidation({ ...tmpValidation, [nameField]: inputValue });
	};

	/**
	 * Input blur (leave field) handler.
	 *
	 * @param {*} e event
	 */
	const inputBlurHandler = (e) => {
		var inputValue = e.target.value;
		const idField = e.target.id;
		const idName = e.target.name;

		switch (idField) {
			case firstID:
				if (inputValue.trim() === "") {
					fieldsErrorState.errorPickupFrom = true;
					setErrorBackgroundPickupFrom(true);
				} else {
					fieldsErrorState.errorPickupFrom = false;
					setErrorBackgroundPickupFrom(false);
				}
				break;
			case "transferto":
				if (inputValue.trim() === "") {
					fieldsErrorState.errorTransferTo = true;
					setErrorBackgroundTransferTo(true);
				} else {
					fieldsErrorState.errorTransferTo = false;
					setErrorBackgroundTransferTo(false);
				}
				break;
			case "passengerName":
				if (inputValue.trim() === "") {
					fieldsErrorState.errorPassengerName = true;
					setErrorBackgroundPassengerName(true);
				} else {
					fieldsErrorState.errorPassengerName = false;
					setErrorBackgroundPassengerName(false);
				}
				break;
			case "bookedFrom":
				if (inputValue.trim() === "") {
					fieldsErrorState.errorBookedFrom = true;
					setErrorBackgroundBookedFrom(true);
				} else {
					fieldsErrorState.errorBookedFrom = false;
					setErrorBackgroundBookedFrom(false);
				}
				break;
			case "phone":
				if (inputValue.trim() === "") {
					fieldsErrorState.errorPhone = true;
					setErrorBackgroundPhone(true);
				} else {
					fieldsErrorState.errorPhone = false;
					setErrorBackgroundPhone(false);
				}
				break;
			case "email":
				if (inputValue.trim() === "") {
					fieldsErrorState.errorEmail = true;
					setErrorBackgroundEmail(true);
				} else {
					fieldsErrorState.errorEmail = false;
					setErrorBackgroundEmail(false);
				}
				break;
		}

		switch (idName) {
			case "pickupDate":
				if (inputValue.trim() === "") {
					fieldsErrorState.errorPickupDate = true;
					setErrorBackgroundDate(true);
				} else {
					fieldsErrorState.errorPickupDate = false;
				}
				break;
			case "pickupTime":
				if (inputValue.trim() === "") {
					fieldsErrorState.errorPickupTime = true;
					setErrorBackgroundTime(true);
				} else {
					fieldsErrorState.errorPickupTime = false;
				}
				break;
		}
	};

	/**
	 * Formular input fields validation.
	 *
	 * @returns true if validation is successfully, otherwise false
	 */
	const validateInputs = () => {
		let validateSuccess = true;

		if (document.getElementById(firstID).value === "") {
			validateSuccess = false;
		}

		if (document.getElementById("transferto").value === "") {
			validateSuccess = false;
		}

		if (!validation.pickupDate) {
			validateSuccess = false;
		}

		if (!validation.pickupTime) {
			validateSuccess = false;
		}

		if (!validation.passengerName) {
			validateSuccess = false;
		}

		if (!validation.bookedFrom) {
			validateSuccess = false;
		}

		if (!validation.phone) {
			validateSuccess = false;
		}

		if (!validation.email) {
			validateSuccess = false;
		}

		if (acceptancePeriodMissed) {
			validateSuccess = false;
		}

		if (!termsOfUseChecked) {
			validateSuccess = false;
		}

		if (!carSelected) {
			validateSuccess = false;
		}

		return validateSuccess;
	};

	/**
	 * Show payment drop down JSX UI-Component.
	 *
	 * @returns JSX-Code for payment
	 */
	const showPayment = () => {
		return (
			<div className="col-12 md:col-4">
				<span className="p-float-label">
					<Dropdown
						value={selectedPayment}
						options={payment}
						onChange={selectPaymentHandler}
						optionLabel="label"
					/>
					<label htmlFor="bookedFrom">
						{t("formular.placeholder-payment")}
					</label>
				</span>
			</div>
		);
	};

	/**
	 * Set background color of company box, dependent on username.
	 *
	 * @returns JSX-Code
	 */
	const setCompanyInfoBoxBgColor = () => {
		let color = null;

		switch (userRegistrationDataObj.username) {
			case "royal-transfer":
				color = "#e8ae3a";
				break;
			default:
				color = "#fcfcfc";
				break;
		}

		return color;
	};

	/**
	 * Show company info box.
	 *
	 * @returns JSX-Code for showing company info box.
	 */
	const showCompanyInfoBox = () => {
		if (whitelabel === "1") return null;

		return (
			<div
				className="grid p-fluid"
				style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "15px" }}
			>
				<div className="col-12 md:col-12">
					<div style={{ marginTop: "15px" }} />
					<Card
						style={{
							backgroundColor: "#fcfcfc",
						}}
					>
						<div
							style={{
								fontSize: "1.2rem",
								fontWeight: "bold",
								marginBottom: "10px",
							}}
						>
							{userRegistrationDataObj.companynameOrOwner}
						</div>
						<div
							style={{
								fontSize: "1.2rem",
								marginBottom: "10px",
							}}
						>
							{userRegistrationDataObj.services}
						</div>
						<div
							style={{
								fontSize: "1.2rem",
							}}
						>
							{userRegistrationDataObj.locations}
						</div>
					</Card>
				</div>
			</div>
		);
	};

	/**
	 * Show payment methods JSX-area if stripeConnectID in tblRegistration is set.
	 *
	 * @returns JSX-Code for showing payment methods JSX-area
	 */
	const showPaymentMethods = () => {
		return userRegistrationDataObj.stripeConnectID !== null &&
			userRegistrationDataObj.stripeConnectID !== "" ? (
			<>
				<div className="card flex mt-4 mb-3">
					<div className="flex">
						<Checkbox
							inputId="creditCard"
							checked={ingredient === constants.PAYMENT_METHOD_CREDIT_CARD}
							onChange={(e) => controlPaymentMethod(e)}
						/>
						<label htmlFor="creditCard" className="ml-2">
							{t("screentext.screen-text-payment-methods")}
						</label>
					</div>
				</div>

				{cardPaymentEnabled ? (
					<>
						<div className="mb-4">
							<CardElement
								id="stripe-card-element"
								options={cardElementOptions}
							/>
						</div>
						{/*}
            <span className="p-input-icon-left">
              <i className="pi pi-users" style={{ paddingLeft: "20px" }} />
              <span className="p-float-label">
                <InputNumber
                  id="tip"
                  name="tip"
                  value={tipValue}
                  mode="decimal"
                  onValueChange={(e) => setTipValue(e.value)}
                  showButtons
                  min={0}
                  max={10000}
                />
                <label htmlFor="tip">{t("formular.placeholder-tip")}</label>
              </span>
            </span>
            {*/}
					</>
				) : null}
			</>
		) : null;
	};

	/**
	 * Show address fields for receipt, if user selected
	 * "On company bill" in payment dropdown selection.
	 *
	 */
	const showReceiptAddressFields = () => {
		return (
			<>
				<div
					className="grid p-fluid"
					style={{
						paddingLeft: "20px",
						paddingRight: "20px",
						marginTop: "1px",
					}}
				>
					<div className="col-12">
						<Card
							style={{
								backgroundColor: "#fcfcfc",
							}}
						>
							<div className="grid p-fluid">
								<div className="col-12">
									<div
										style={{
											fontSize: "1.2rem",
											fontWeight: "bold",
											marginBottom: "10px",
										}}
									>
										{t("screentext.screen-text-payment-address")}
									</div>
								</div>

								<div className="col-12 md:col-4">
									<span className="p-float-label">
										<InputText
											id="companyName"
											name="companyName"
											maxlength="100"
										/>
										<label htmlFor="companyName">
											{t("formular.placeholder-company-name")}
										</label>
									</span>
								</div>
								<div className="col-12 md:col-4">
									<span className="p-float-label">
										<InputText
											id="companyStreet"
											name="companyStreet"
											maxlength="250"
										/>
										<label htmlFor="companyStreet">
											{t("formular.placeholder-company-street")}
										</label>
									</span>
								</div>
								<div className="col-12 md:col-4">
									<span className="p-float-label">
										<InputText
											id="companyZip"
											name="companyZip"
											maxlength="250"
										/>
										<label htmlFor="companyZip">
											{t("formular.placeholder-company-zip")}
										</label>
									</span>
								</div>
								<div className="col-12 md:col-4">
									<span className="p-float-label">
										<InputText
											id="companyLocation"
											name="companyLocation"
											maxlength="250"
										/>
										<label htmlFor="companyLocation">
											{t("formular.placeholder-company-location")}
										</label>
									</span>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</>
		);
	};

	/**
	 * Show tariff regulation text if user has configured.
	 *
	 * @returns JSX-Code for showing tariff regulation text
	 */
	const showTariffRegulationText = () => {
		return (
			<>
				{userRegistrationDataObj.showTariffRegulationText === 1 ? (
					<div>
						<p>{t("infotext.info-text-tariff-regulation")}</p>
					</div>
				) : null}
			</>
		);
	};

	/**
	 * Terms of use check box change handler.
	 *
	 * @param {*} e event
	 */
	const ckeckBoxChangeHandler = (e) => {
		setTermsOfUseChecked(e.checked);

		if (e.checked) {
			fieldsErrorState.errorTerms = false;
			setErrorBackgroundTerms(false);
		} else {
			fieldsErrorState.errorTerms = true;
			setErrorBackgroundTerms(true);
		}
	};

	/**
	 * Show terms of use & data protection check box.
	 *
	 * @returns JSX-Code for showing use & data protection check box
	 */
	const showCheckboxTermsOfUse = () => {
		return errorBackgroundTerms ? (
			<div className="error-checkbox-card">
				<div className="field-checkbox">
					<Checkbox
						inputId="termsOfUse"
						checked={termsOfUseChecked}
						onChange={(e) => ckeckBoxChangeHandler(e)}
					/>
					{userRegistrationDataObj.agbLink !== "" &&
					userRegistrationDataObj.dataprotectionLink !== ""
						? ownTerms()
						: standardTerms()}
				</div>
			</div>
		) : (
			<div className="checkbox-card">
				<div className="field-checkbox">
					<Checkbox
						inputId="termsOfUse"
						checked={termsOfUseChecked}
						onChange={(e) => ckeckBoxChangeHandler(e)}
					/>
					{userRegistrationDataObj.agbLink !== "" &&
					userRegistrationDataObj.dataprotectionLink !== ""
						? ownTerms()
						: standardTerms()}
				</div>
			</div>
		);
	};

	/**
	 * Show own configured terms of use & dataprotection links.
	 *
	 * @returns JSX-Code
	 */
	const ownTerms = () => {
		return (
			<label htmlFor="termsOfUse">
				{t("screentext.screen-text-termsofuse-part1")}{" "}
				<a
					href={userRegistrationDataObj.agbLink}
					target="_blank"
					rel="noreferrer"
				>
					{t("screentext.screen-text-termsofuse-part2")}
				</a>{" "}
				{t("screentext.screen-text-termsofuse-part3")}{" "}
				<a
					href={userRegistrationDataObj.dataprotectionLink}
					target="_blank"
					rel="noreferrer"
				>
					{t("screentext.screen-text-termsofuse-part4")}
				</a>{" "}
				*
			</label>
		);
	};

	/**
	 * Show standard terms of use & dataprotection links.
	 *
	 * @returns JSX-Code
	 */
	const standardTerms = () => {
		if (whitelabel === "1") {
			if (
				userRegistrationDataObj.agbLink !== "" ||
				userRegistrationDataObj.dataprotectionLink !== ""
			) {
				return (
					<label htmlFor="termsOfUse">
						{t("screentext.screen-text-termsofuse-part1")}{" "}
						<a
							href={userRegistrationDataObj.agbLink}
							target="_blank"
							rel="noreferrer"
						>
							{t("screentext.screen-text-termsofuse-part2")}
						</a>{" "}
						{t("screentext.screen-text-termsofuse-part3")}{" "}
						<a
							href={userRegistrationDataObj.dataprotectionLink}
							target="_blank"
							rel="noreferrer"
						>
							{t("screentext.screen-text-termsofuse-part4")}
						</a>{" "}
						*
					</label>
				);
			} else {
				return (
					<label htmlFor="termsOfUse">
						{t("screentext.screen-text-termsofuse-part1")}{" "}
						<a href="">{t("screentext.screen-text-termsofuse-part2")}</a>{" "}
						{t("screentext.screen-text-termsofuse-part3")}{" "}
						<a href="">{t("screentext.screen-text-termsofuse-part4")}</a> *
					</label>
				);
			}
		}

		return (
			<label htmlFor="termsOfUse">
				{t("screentext.screen-text-termsofuse-part1")}{" "}
				<a
					href={
						"https://www." +
						userRegistrationDataObj.username +
						".drivepartner.com/customerinfo.php"
					}
					target="_blank"
					rel="noreferrer"
				>
					{t("screentext.screen-text-termsofuse-part2")}
				</a>{" "}
				{t("screentext.screen-text-termsofuse-part3")}{" "}
				<a
					href={
						"https://www." +
						userRegistrationDataObj.username +
						".drivepartner.com/dataprotection.php"
					}
					target="_blank"
					rel="noreferrer"
				>
					{t("screentext.screen-text-termsofuse-part4")}
				</a>{" "}
				*
			</label>
		);
	};

	/**
	 * Submit formular. Validate input fields
	 * & send data to server after successfully validation.
	 */
	const submitForm = async () => {
		if (
			userRegistrationDataObj.tariff === 2 &&
			userRegistrationDataObj.stripeConnectID !== null
		) {
			if (ingredient === constants.PAYMENT_METHOD_CREDIT_CARD) {
				if (!stripe || !elements) {
					toast.error(t("errortext.error-text-server-error"));
					return;
				}

				const card = elements.getElement(CardElement);
				const result = await stripe.createToken(card);

				if (!validateInputs() || result.error) {
					if (choosenCarCategory.current === "") {
						setShowErrorInfoBox("infobox_error_validation_no_fixpriceroute");
					} else {
						setShowErrorInfoBox("infobox_error_validation");
					}

					setTimeout(function () {
						setShowErrorInfoBox("infobox_empty");
					}, 6000);
				} else {
					confirmDialog({
						position: "bottom",
						message: t("infotext.info-text-sendbooking"),
						header: t("infotext.info-text-sendbooking-dialogheader"),
						icon: "pi pi-question-circle",
						accept: () =>
							sendDataToServerWithCreditCardData(card, result.token),
						reject: () => null,
					});
				}
			} else {
				// Payment on driver (without credit card)
				if (validateInputs()) {
					confirmDialog({
						position: "bottom",
						message: t("infotext.info-text-sendbooking"),
						header: t("infotext.info-text-sendbooking-dialogheader"),
						icon: "pi pi-question-circle",
						accept: () => sendBookingToServer(),
						reject: () => null,
					});
				} else {
					if (choosenCarCategory.current === "") {
						setShowErrorInfoBox("infobox_error_validation_no_fixpriceroute");
					} else {
						setShowErrorInfoBox("infobox_error_validation");
					}

					setTimeout(function () {
						setShowErrorInfoBox("infobox_empty");
					}, 6000);
				}
			}
		} else {
			if (validateInputs()) {
				confirmDialog({
					position: "bottom",
					message: t("infotext.info-text-sendbooking"),
					header: t("infotext.info-text-sendbooking-dialogheader"),
					icon: "pi pi-question-circle",
					accept: () => sendBookingToServer(),
					reject: () => null,
				});
			} else {
				if (choosenCarCategory.current === "") {
					setShowErrorInfoBox("infobox_error_validation_no_fixpriceroute");
				} else {
					setShowErrorInfoBox("infobox_error_validation");
				}

				setTimeout(function () {
					setShowErrorInfoBox("infobox_empty");
				}, 6000);
			}
		}
	};

	/**
	 * Reload booking page to have fresh empty formular screen.
	 */
	const resetForm = () => {
		confirmDialog({
			position: "bottom",
			message: t("infotext.info-text-resetscreen"),
			header: t("infotext.info-text-resetscreen-dialogheader"),
			icon: "pi pi-refresh",
			accept: () => resetScreen(),
			reject: () => null,
		});
	};

	/**
	 * Send booking data now to DrivePartner-Server and
	 * receive response from server.,
	 */
	const sendBookingToServer = () => {
		getFormularValues();
		const bookingData = {
			...getBookingData(),
			paymentMethod: constants.PAYMENT_METHOD_ON_DRIVER,
		};

		setShowErrorInfoBox("wait_circle");

		axios({
			method: "post",
			url: URL_SERV + PHP_FILE_STOREFIXPRICEBOOKINGDATA,
			data: bookingData,
		}).then((response) => {
			if (response.data.Success) {
				setShowErrorInfoBox("infobox_success");
			} else {
				setShowErrorInfoBox("infobox_error_send_bookingdata");
			}

			resetRoute();

			setTimeout(function () {
				setShowErrorInfoBox("infobox_empty");
			}, 6000);
		});
	};

	/**
	 * Send booking & credit card data to DrivePartner-Server
	 * and receive response from server.
	 *
	 * @param {*} cardObj card object from CarElement
	 * @param {*} token generatedtoken
	 */
	const sendDataToServerWithCreditCardData = (cardObj, token) => {
		let locTip = 0;
		let clientSecret = null;

		getFormularValues();

		if (document.getElementById("tip")) {
			locTip = tipValue;
		}

		const bookingData = {
			...getBookingData(),
			actioncode: CREATE_PAYMENT_INTENT,
			paymentMethod: constants.PAYMENT_METHOD_CREDIT_CARD,
			token: token.id,
			cardLast4: token.card.last4,
			cardBrand: token.card.brand,
			tip: locTip,
		};

		setShowErrorInfoBox("wait_circle");

		axios({
			method: "post",
			url: URL_SERV + PHP_FILE_CREATE_PAYMENTINTENT,
			data: bookingData,
		}).then((response) => {
			if (response.data.clientSecret) {
				clientSecret = response.data.clientSecret;
				stripe
					.confirmCardPayment(clientSecret, {
						payment_method: {
							card: cardObj,
						},
					})
					.then(function (result) {
						if (result.error) {
							setShowErrorInfoBox("infobox_error_send_bookingdata");

							resetRoute();

							setTimeout(function () {
								setShowErrorInfoBox("infobox_empty");
							}, 6000);
						} else {
							// Begin: Store booking data now in database
							const newBookingData = {
								...getBookingData(),
								actioncode: SEND_BOOKINGDATA,
								paymentMethod: constants.PAYMENT_METHOD_CREDIT_CARD,
								token: token.id,
								cardLast4: token.card.last4,
								cardBrand: token.card.brand,
								tip: locTip,
							};

							axios({
								method: "post",
								url: URL_SERV + PHP_FILE_STOREFIXPRICEBOOKINGDATA,
								data: newBookingData,
							}).then((response) => {
								if (response.data.Success) {
									setShowErrorInfoBox("infobox_success");
								} else {
									setShowErrorInfoBox("infobox_error_send_bookingdata");
								}

								resetRoute();

								setTimeout(function () {
									setShowErrorInfoBox("infobox_empty");
								}, 6000);
							});
							// End: Store booking data now in database
						}
					});
			} else {
				setShowErrorInfoBox("infobox_error_send_bookingdata");
			}
		});
	};

	/**
	 * Reset route components incl. Goole map & car offering.
	 */
	const resetRoute = () => {
		document.getElementById(firstID).value = "";
		document.getElementById("transferto").value = "";
		calcFaresFinished = false;
		acceptancePeriodMissed = false;
		setValidation({
			...validation,
			//pickupDate: moment().toDate(),
			pickupDate: null,
			pickupTime: null,
		});
		setErrorBackgroundPickupFrom(true);
		setErrorBackgroundTransferTo(true);

		addCounter = 0;
		waypoints.length = 0;
		setFormValues([{ name: "", id: firstID }]);
		waypoint1 = "";
		waypoint2 = "";
		waypoint3 = "";
		tmpWaypoint1 = "";
		tmpWaypoint2 = "";
		tmpWaypoint3 = "";

		setErrorBackgroundTime(true);
		directionsRenderer.setMap(null);
		directionsRenderer = null;
		directionsRenderer = new window.google.maps.DirectionsRenderer();
		setRoutingResultValues(null);
		setSelectedFixRoute(null);
		setRouteSelected(false);
	};

	/**
	 * Reset route components incl. Goole map & car offering
	 * in case of post code missing pickup from.
	 */
	const resetRouteFrom = () => {
		document.getElementById(firstID).value = "";
		calcFaresFinished = false;

		addCounter = 0;
		waypoints.length = 0;
		waypoint1 = "";
		waypoint2 = "";
		waypoint3 = "";

		setErrorBackgroundPickupFrom(true);
		directionsRenderer.setMap(null);
		directionsRenderer = null;
		directionsRenderer = new window.google.maps.DirectionsRenderer();
		setRoutingResultValues(null);
	};

	/**
	 * Reset route components incl. Goole map & car offering
	 * in case of post code missing transfer to.
	 */
	const resetRouteTransferTo = () => {
		document.getElementById("transferto").value = "";
		calcFaresFinished = false;

		addCounter = 0;
		waypoints.length = 0;
		waypoint1 = "";
		waypoint2 = "";
		waypoint3 = "";

		setErrorBackgroundTransferTo(true);
		directionsRenderer.setMap(null);
		directionsRenderer = null;
		directionsRenderer = new window.google.maps.DirectionsRenderer();
		setRoutingResultValues(null);
	};

	/**
	 * Reload booking screen.
	 */
	const resetScreen = () => {
		//window.location.reload();
		navigate(0);
	};

	/**
	 * Get all booking formular values and store into special
	 * booking data formular values object.
	 */
	const getFormularValues = () => {
		const formValuesLength = formValues.length;
		bookingDataFormularValuesObj.pickupFrom = startAddress;
		bookingDataFormularValuesObj.transferTo = endAddress;
		bookingDataFormularValuesObj.waypoint1 = "";
		bookingDataFormularValuesObj.waypoint2 = "";
		bookingDataFormularValuesObj.waypoint3 = "";

		userCalcRouteObj.choosenCarOffer = choosenCarCategory.current;

		if (addCounter > 0) {
			// Get all waypoints if avaiable
			if (addCounter === 1) {
				bookingDataFormularValuesObj.waypoint1 = waypoint1;
			}

			if (addCounter === 2) {
				bookingDataFormularValuesObj.waypoint1 = waypoint1;
				bookingDataFormularValuesObj.waypoint2 = waypoint2;
			}

			if (addCounter === 3) {
				bookingDataFormularValuesObj.waypoint1 = waypoint1;
				bookingDataFormularValuesObj.waypoint2 = waypoint2;
				bookingDataFormularValuesObj.waypoint3 = waypoint3;
			}
		} else {
			bookingDataFormularValuesObj.waypoint1 = "";
			bookingDataFormularValuesObj.waypoint2 = "";
			bookingDataFormularValuesObj.waypoint3 = "";
		}

		bookingDataFormularValuesObj.pickupDate = moment(validation.pickupDate)
			.format("DD/MM/YYYY")
			.toString();
		bookingDataFormularValuesObj.pickupTime = moment(validation.pickupTime)
			.format("HH:mm")
			.toString();
		bookingDataFormularValuesObj.payment = selectedPayment;
		bookingDataFormularValuesObj.passengerName = document
			.getElementById("passengerName")
			.value.trim();
		bookingDataFormularValuesObj.mobilePhone = document
			.getElementById("mobile")
			.value.trim();
		bookingDataFormularValuesObj.pax = paxValue;
		bookingDataFormularValuesObj.airport = document
			.getElementById("airport")
			.value.trim();
		bookingDataFormularValuesObj.comment = document
			.getElementById("shortComment")
			.value.trim();
		bookingDataFormularValuesObj.bookedFrom = document
			.getElementById("bookedFrom")
			.value.trim();
		bookingDataFormularValuesObj.phone = document
			.getElementById("phone")
			.value.trim();
		bookingDataFormularValuesObj.email = document
			.getElementById("email")
			.value.trim();

		bookingDataFormularValuesObj.calcRouteObj = userCalcRouteObj;

		// Receipt address data (in case of "On company bill")
		if (document.getElementById("companyName")) {
			bookingDataFormularValuesObj.companyName = document
				.getElementById("companyName")
				.value.trim();
		}

		if (document.getElementById("companyStreet")) {
			bookingDataFormularValuesObj.companyStreet = document
				.getElementById("companyStreet")
				.value.trim();
		}

		if (document.getElementById("companyZip")) {
			bookingDataFormularValuesObj.companyZip = document
				.getElementById("companyZip")
				.value.trim();
		}

		if (document.getElementById("companyLocation")) {
			bookingDataFormularValuesObj.companyLocation = document
				.getElementById("companyLocation")
				.value.trim();
		}
	};

	/**
	 * Get booking data for sending booking data to server.
	 *
	 * @returns booking data object
	 */
	const getBookingData = () => {
		let comment = "";
		let vat = "";

		if (formValues.length === 2) {
			tmpWaypoint1 = document.getElementById(formValues[1]["id"]).value;
		} else if (formValues.length === 3) {
			tmpWaypoint1 = document.getElementById(formValues[1]["id"]).value;
			tmpWaypoint2 = document.getElementById(formValues[2]["id"]).value;
		} else if (formValues.length === 4) {
			tmpWaypoint1 = document.getElementById(formValues[1]["id"]).value;
			tmpWaypoint2 = document.getElementById(formValues[2]["id"]).value;
			tmpWaypoint3 = document.getElementById(formValues[3]["id"]).value;
		}

		// Interims solution! Write credit card payd text in front of comment
		if (
			userRegistrationDataObj.tariff === 2 &&
			ingredient === constants.PAYMENT_METHOD_CREDIT_CARD
		) {
			comment =
				t("infotext.info-text-payed-with-credit-card") +
				" " +
				bookingDataFormularValuesObj.comment;
		} else {
			comment = bookingDataFormularValuesObj.comment;
		}

		console.log("choosenCarCategory: " + choosenCarCategory.current);

		// Fetch VAT from route data
		if (choosenCarCategory.current === "Limousine Business") {
			vat = selectedFixRoute.MWST_CATEGORY_LIMBUSSINES;
		} else if (choosenCarCategory.current === "Business FirstClass") {
			vat = selectedFixRoute.MWST_CATEGORY_FIRST_CLASS;
		} else if (choosenCarCategory.current === "Business Van") {
			vat = selectedFixRoute.MWST_CATEGORY_VAN;
		} else if (choosenCarCategory.current === "Business XL") {
			vat = selectedFixRoute.MWST_CATEGORY_BUSXL;
		}

		console.log("vat: " + vat);

		return {
			actionCode: SEND_BOOKINGDATA,
			username: userRegistrationDataObj.username,
			language: selectedLanguage.language,
			pickupFrom: document.getElementById(firstID).value,
			transferTo: document.getElementById("transferto").value,
			waypoint1: tmpWaypoint1,
			waypoint2: tmpWaypoint2,
			waypoint3: tmpWaypoint3,
			pickupDate: bookingDataFormularValuesObj.pickupDate,
			pickupTime: bookingDataFormularValuesObj.pickupTime,
			payment: bookingDataFormularValuesObj.payment,
			passengerName: bookingDataFormularValuesObj.passengerName,
			passengerMobile: bookingDataFormularValuesObj.mobilePhone,
			pax: bookingDataFormularValuesObj.pax,
			airport: bookingDataFormularValuesObj.airport,
			comment: comment,
			orderedBy: bookingDataFormularValuesObj.bookedFrom,
			phone: bookingDataFormularValuesObj.phone,
			email: bookingDataFormularValuesObj.email,
			calcPrice: calcPrice,
			choosenCarOffer: choosenCarCategory.current,
			stripeConnectID: userRegistrationDataObj.stripeConnectID,
			companyName: bookingDataFormularValuesObj.companyName,
			companyStreet: bookingDataFormularValuesObj.companyStreet,
			companyZip: bookingDataFormularValuesObj.companyZip,
			companyLocation: bookingDataFormularValuesObj.companyLocation,
			vat: vat,
			carCategoryAlternativeName: carCategoryAlternativeName.current,
		};
	};

	/**
	 * Set background color for "pickupfrom" validation.
	 *
	 * @returns color code
	 */
	const setBackgroundColorPickupFrom = () => {
		if (errorBackgroundPickupFrom) {
			return "#FBF4F4FF";
		} else {
			return "";
		}
	};

	/**
	 * Set background color for "transferto" validation.
	 *
	 * @returns color code
	 */
	const setBackgroundColorTransferTo = () => {
		if (errorBackgroundTransferTo) {
			return "#FBF4F4FF";
		} else {
			return "";
		}
	};

	/**
	 * Set background color for "pickupDate" validation.
	 *
	 * @returns color code
	 */
	const setBackgroundColorDate = () => {
		if (errorBackgroundDate) {
			return "#FBF4F4FF";
		} else {
			return "";
		}
	};

	/**
	 * Set background color for "pickupTime" validation.
	 *
	 * @returns color code
	 */
	const setBackgroundColorTime = () => {
		if (errorBackgroundTime) {
			return "#FBF4F4FF";
		} else {
			return "";
		}
	};

	/**
	 * Set background color for "passengerName" validation.
	 *
	 * @returns color code
	 */
	const setBackgroundColorPassengerName = () => {
		if (errorBackgroundPassengerName) {
			return "#FBF4F4FF";
		} else {
			return "";
		}
	};

	/**
	 * Set background color for "bookedFrom" validation.
	 *
	 * @returns color code
	 */
	const setBackgroundColorBookedFrom = () => {
		if (errorBackgroundBookedFrom) {
			return "#FBF4F4FF";
		} else {
			return "";
		}
	};

	/**
	 * Set background color for "phone" validation.
	 *
	 * @returns color code
	 */
	const setBackgroundColorPhone = () => {
		if (errorBackgroundPhone) {
			return "#FBF4F4FF";
		} else {
			return "";
		}
	};

	/**
	 * Set background color for "email" validation.
	 *
	 * @returns color code
	 */
	const setBackgroundColorEmail = () => {
		if (errorBackgroundEmail) {
			return "#FBF4F4FF";
		} else {
			return "";
		}
	};

	/**
	 * Show booking success info box or error info box.
	 *
	 * @returns JSX-Code for bokking success info box or error info box.
	 */
	const showInfoBox = () => {
		if (showErrorInfoBox === "infobox_success") {
			return (
				<div className="success-box">
					<table>
						<tr>
							<td>
								<i
									className="pi pi-check-circle"
									style={{ fontSize: "2em", paddingRight: "20px" }}
								></i>
							</td>
							<td style={{ verticalAlign: "center" }}>
								<p>{t("successtext.success-store-Booking-text")}</p>
								<p style={{ fontSize: "1.3em", fontWeight: "bold" }}>
									{t("successtext.success-store-Booking-text2")}
								</p>
							</td>
						</tr>
					</table>
				</div>
			);
		} else if (showErrorInfoBox === "infobox_error_validation") {
			return (
				<div className="error-box">
					<table>
						<tr>
							<td>
								<i
									className="pi pi-exclamation-circle"
									style={{ fontSize: "2em", paddingRight: "20px" }}
								></i>
							</td>
							<td style={{ verticalAlign: "center" }}>
								{t("errortext.error-validation-text")}
							</td>
						</tr>
					</table>
				</div>
			);
		} else if (showInfoBox === "infobox_error_validation_no_fixpriceroute") {
			return (
				<div className="error-box">
					<table>
						<tr>
							<td>
								<i
									className="pi pi-exclamation-circle"
									style={{ fontSize: "2em", paddingRight: "20px" }}
								></i>
							</td>
							<td style={{ verticalAlign: "center" }}>
								{t("errortext.error-validation-text-no-fixpriceroute")}
							</td>
						</tr>
					</table>
				</div>
			);
		} else if (showErrorInfoBox === "infobox_error_postcode") {
			return (
				<div className="error-box">
					<table>
						<tr>
							<td>
								<i
									className="pi pi-exclamation-circle"
									style={{ fontSize: "2em", paddingRight: "20px" }}
								></i>
							</td>
							<td style={{ verticalAlign: "center" }}>
								{t("errortext.error-postcode-text")}
							</td>
						</tr>
					</table>
				</div>
			);
		} else if (showErrorInfoBox === "infobox_error_send_bookingdata") {
			return (
				<div className="error-box">
					<table>
						<tr>
							<td>
								<i
									className="pi pi-exclamation-circle"
									style={{ fontSize: "2em", paddingRight: "20px" }}
								></i>
							</td>
							<td style={{ verticalAlign: "center" }}>
								{t("errortext.error-sendbookingdata-text")}
							</td>
						</tr>
					</table>
				</div>
			);
		} else if (showErrorInfoBox === "wait_circle") {
			return <ProgressSpinner style={{ width: "60px", height: "60px" }} />;
		} else {
			return null;
		}
	};

	/**
	 * Show Listbox with configured fix price routes.
	 *
	 * @returns JSX-Code for fixed price routes Listbox
	 */
	const showFixPriceRoutesTable = () => {
		const fixedRoutes = userData.fixPriceRoutes;

		return (
			<>
				<label htmlFor="fixedRoutes" style={{ fontWeight: "bold" }}>
					{t("formular.fixedRoutes")}
				</label>
				<ListBox
					id="fixedRoutes"
					name="fixedRoutes"
					value={selectedFixRoute}
					onChange={(e) => showCarModelBoxesForFixPriceRoutes(e)}
					options={fixedRoutes}
					optionLabel="ROUTE_NAME"
					className="w-full mt-2"
					listStyle={{ maxHeight: "287px", height: "287px" }}
				/>
			</>
		);
	};

	/**
	 * Show price info box(es) for selection the car model.
	 * @param {*} e selcted ListBox value
	 */
	const showCarModelBoxesForFixPriceRoutes = (e) => {
		choosenCarCategory.current = "";
		calcFaresFinished = true;
		let isSet = false;

		// Set default first car category values
		const selectedRouteObj = e.value;

		setRouteSelected(true);

		if (selectedRouteObj.CARMODELS_BUSINESS !== "") {
			calcPrice = selectedRouteObj.PRICE_BUSINESS;
			choosenCarCategory.current = selectedRouteObj.CARMODELS_BUSINESS;
			isSet = true;
		}

		if (selectedRouteObj.CARMODELS_FIRSTCLASS !== "") {
			if (!isSet) {
				calcPrice = selectedRouteObj.PRICE_FIRSTCLASS;
				choosenCarCategory.current = selectedRouteObj.CARMODELS_FIRSTCLASS;
				isSet = true;
			}
		}

		if (selectedRouteObj.CARMODELS_VAN !== "") {
			if (!isSet) {
				calcPrice = selectedRouteObj.PRICE_VAN;
				choosenCarCategory.current = selectedRouteObj.CARMODELS_VAN;
				isSet = true;
			}
		}

		if (selectedRouteObj.CARMODELS_BUSXL !== "") {
			if (!isSet) {
				calcPrice = selectedRouteObj.PRICE_BUSXL;
				choosenCarCategory.current = selectedRouteObj.CARMODELS_BUSXL;
			}
		}

		setSelectedFixRoute(e.value); // Trigger rerendering, because state is changed
	};

	/**
	 * Control the first left column. If no fix price route
	 * is selected, set width of column to "8", otherwise to "4".
	 *
	 * @returns JSX-Code
	 */
	const controlFirstLeftColumn = () => {
		if (!routeSelected) {
			return (
				<div className="col-12 md:col-8">
					{/* Fixed price routes */}
					{showFixPriceRoutesTable()}
				</div>
			);
		} else {
			return (
				<div className="col-12 md:col-4">
					{/* Fixed price routes */}
					{showFixPriceRoutesTable()}
				</div>
			);
		}
	};

	/**
	 * Control the first middle column. If no fix price route
	 * is selected, set width of column to "0", otherwise to "4".
	 *
	 * @returns JSX-Code
	 */
	const controlFirstMiddleColumn = () => {
		if (!routeSelected) {
			return null;
		} else {
			return (
				<div className="col-12 md:col-4">
					<ChooseCarCategoryOfferFixPrice
						userData={userData}
						selectedFixRoute={selectedFixRoute}
						calcFaresFinished={calcFaresFinished}
						userCalcRouteObj={userCalcRouteObj}
						userRegistrationDataObj={userRegistrationDataObj}
						choosenCarOffer={choosenCarOffer}
					/>
				</div>
			);
		}
	};

	/**
	 * Set the first right column.
	 *
	 * @returns JSX-Code
	 */
	const setFirstRightColumn = () => {
		return (
			<div className="col-12 md:col-4">
				{formValues.map((element, index) => (
					<div style={{ marginBottom: "15px" }} key={index}>
						{index === 0 ? (
							<span className="p-float-label">
								<InputText
									id={element.id}
									name={nameRouteInputField(element, index)}
									style={{
										width: "100%",
										backgroundColor: setBackgroundColorPickupFrom(),
									}}
									placeholder=" "
									maxlength="250"
									onBlur={(e) => inputBlurHandler(e)}
								/>
								<label htmlFor="pickupfrom">
									{t("formular.placeholder-pickupfrom")}
								</label>
							</span>
						) : (
							<>
								<div className="p-inputgroup">
									<span className="p-float-label">
										<InputText
											id={element.id}
											style={{ width: "100%" }}
											placeholder=" "
											maxlength="250"
										/>
										<label htmlFor={element.id}>
											{t("formular.placeholder-stoppoint")}
										</label>
									</span>

									{index === formValues.length - 1 && formValues.length > 1 ? (
										<Button
											icon="pi pi-trash"
											onClick={() => removeWaypoint(index)}
										/>
									) : null}
								</div>
							</>
						)}
					</div>
				))}
				<div className="button-section">
					{addCounter < 3 ? (
						<Button
							label={t("formular.btn-add-stoppoint")}
							icon="pi pi-plus"
							onClick={() => addWaypoint()}
							style={{ width: "160px" }}
							disabled={disableAddBtn}
						/>
					) : null}
				</div>
				<div style={{ marginTop: "15px" }} />
				<span className="p-float-label">
					<InputText
						id="transferto"
						name="transferto"
						placeholder=" "
						style={{
							width: "100%",
							backgroundColor: setBackgroundColorTransferTo(),
						}}
						maxlength="250"
						onBlur={(e) => inputBlurHandler(e)}
					/>
					<label htmlFor="transferto">
						{t("formular.placeholder-transferto")}
					</label>
				</span>
				<span className="p-float-label">
					<Calendar
						id="pickupDate"
						name="pickupDate"
						minDate={minDate}
						value={validation.pickupDate}
						dateFormat="dd/mm/yy"
						onChange={(e) => changeDateHandler(e)}
						onBlur={(e) => inputBlurHandler(e)}
						showIcon
						style={{
							width: "100%",
							marginTop: "15px",
							backgroundColor: setBackgroundColorDate(),
						}}
						locale="en"
					/>
					<label htmlFor="pickupDate">
						{t("formular.placeholder-pickupdate")}
					</label>
				</span>
				<span className="p-float-label">
					<Calendar
						id="pickupTime"
						name="pickupTime"
						value={validation.pickupTime}
						onChange={(e) => changeTimeHandler(e)}
						onBlur={(e) => inputBlurHandler(e)}
						timeOnly
						hourFormat="24"
						readOnlyInput
						showIcon
						stepMinute={5}
						icon="pi pi-clock"
						style={{
							width: "100%",
							marginTop: "15px",
							backgroundColor: setBackgroundColorTime(),
						}}
					/>
					<label htmlFor="pickupTime">
						{t("formular.placeholder-pickuptime")}
					</label>
				</span>
				{acceptancePeriodMissed ? (
					<span className="error-text">
						{t("errortext.error-acceptancePeriodMissed-part1") +
							userRegistrationDataObj.annahmefrist +
							t("errortext.error-acceptancePeriodMissed-part2")}
					</span>
				) : !validation.pickupTimeError ? null : (
					<span className="error-text">
						{t("errortext.error-pickupTime-validation")}
					</span>
				)}
			</div>
		);
	};

	/**
	 * Show function text links if user has functions
	 * (e.g. fix price, hour booking, ...) configured.
	 *
	 * @returns JSX code for function text links
	 */
	const showFunctionLinks = () => {
		if (userData.fixPrice !== false) {
			return (
				<>
					<div style={{ marginLeft: "19px", marginTop: "15px" }}>
						<Button
							label={t("buttons.btn-text-ab-booking")}
							className="p-button-primary p-button-text"
							icon="pi pi-car"
							iconPos="right"
							onClick={() => navigate("/")}
						/>
					</div>
				</>
			);
		} else {
			return null;
		}
	};

	/**
	 * Enable/Disable Stripe credit card element.
	 *
	 * @param {*} e Event - driver or card selected
	 */
	const controlPaymentMethod = (e) => {
		if (e.checked) {
			setCardPaymentEnabled(true);
			setIngredient(constants.PAYMENT_METHOD_CREDIT_CARD);
		} else {
			setCardPaymentEnabled(false);
			setIngredient(constants.PAYMENT_METHOD_ON_DRIVER);
		}
	};

	//--------------------------------------------------------------
	//--- Returns JSX code for "BookingScreen" ---------------------
	//--------------------------------------------------------------
	return (
		<>
			<Header userData={userData} style={{ left: "0", right: "0" }} />
			<ConfirmDialog />
			{showCompanyInfoBox()}
			{showFunctionLinks()}
			<div
				id="jump-start"
				className="grid p-fluid"
				style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "15px" }}
			>
				{/* Begin: Left column */}
				{controlFirstLeftColumn()}

				{/* Begin: Left column */}
				{controlFirstMiddleColumn()}

				{/* Begin: Right column */}
				{setFirstRightColumn()}
			</div>
			<div
				className="grid p-fluid"
				style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "1px" }}
			>
				{showPayment()}

				<div className="col-12 md:col-4">
					<span className="p-float-label">
						<InputText
							id="passengerName"
							name="passengerName"
							value={validation.passengerName}
							onChange={(e) => inputChangeHandler(e)}
							onBlur={(e) => inputBlurHandler(e)}
							maxlength="100"
							style={{ backgroundColor: setBackgroundColorPassengerName() }}
						/>
						<label htmlFor="bookedFrom">
							{t("formular.placeholder-passengername")}
						</label>
					</span>
				</div>
				<div className="col-12 md:col-4">
					<span className="p-float-label">
						<InputText
							id="mobile"
							name="mobile"
							//keyfilter="int"
							maxlength="50"
						/>
						<label htmlFor="bookedFrom">
							{t("formular.placeholder-mobile")}
						</label>
					</span>
				</div>

				<div className="col-12 md:col-4">
					<span className="p-input-icon-left">
						<i className="pi pi-users" style={{ paddingLeft: "20px" }} />
						<span className="p-float-label">
							<InputNumber
								id="pax"
								name="pax"
								value={paxValue}
								mode="decimal"
								onValueChange={(e) => setPaxValue(e.value)}
								showButtons
								min={1}
								max={60}
							/>
							<label htmlFor="pax">{t("formular.placeholder-pax")}</label>
						</span>
					</span>
				</div>
				<div className="col-12 md:col-4">
					<span className="p-float-label">
						<InputText id="airport" name="airport" maxlength="250" />
						<label htmlFor="airport">{t("formular.placeholder-airport")}</label>
					</span>
				</div>
				<div className="col-12 md:col-4">
					<span className="p-float-label">
						<InputText id="shortComment" name="shortComment" maxlength="250" />
						<label htmlFor="shortComment">
							{t("formular.placeholder-comment-examples")}
						</label>
					</span>
				</div>

				<div className="col-12 md:col-4">
					<span className="p-float-label">
						<InputText
							id="bookedFrom"
							name="bookedFrom"
							value={validation.bookedFrom}
							onChange={(e) => inputChangeHandler(e)}
							onBlur={(e) => inputBlurHandler(e)}
							maxlength="250"
							style={{ backgroundColor: setBackgroundColorBookedFrom() }}
						/>
						<label htmlFor="bookedFrom">
							{t("formular.placeholder-bookedfrom")}
						</label>
					</span>
				</div>
				<div className="col-12 md:col-4">
					<span className="p-float-label">
						<InputText
							id="phone"
							name="phone"
							value={validation.phone}
							//keyfilter="pint"
							onChange={(e) => inputChangeHandler(e)}
							onBlur={(e) => inputBlurHandler(e)}
							maxlength="50"
							style={{ backgroundColor: setBackgroundColorPhone() }}
						/>
						<label htmlFor="phone">{t("formular.placeholder-phone")}</label>
					</span>
				</div>
				<div className="col-12 md:col-4">
					<span className="p-float-label">
						<InputText
							id="email"
							name="email"
							value={validation.email}
							keyfilter="email"
							onChange={(e) => inputChangeHandler(e)}
							onBlur={(e) => inputBlurHandler(e)}
							maxlength="250"
							style={{ backgroundColor: setBackgroundColorEmail() }}
						/>
						<label htmlFor="email">{t("formular.placeholder-email")}</label>
					</span>
					{emailError ? (
						<span className="error-text">
							{t("errortext.error-email-validation")}
						</span>
					) : null}
				</div>
			</div>

			{selectedPayment === constants.PAYMENT_ON_COMPANY_BILL
				? showReceiptAddressFields()
				: null}

			<div
				className="grid p-fluid"
				style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "1px" }}
			>
				<div className="col-12 md:col-4">
					{userRegistrationDataObj.tariff === 2 ? showPaymentMethods() : null}
					<div className="mb-4" />
					{showTariffRegulationText()}
					{showCheckboxTermsOfUse()}
				</div>
			</div>

			<div
				className="grid p-fluid"
				style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "1px" }}
			>
				<div className="col-12">{showInfoBox()}</div>
			</div>

			<div
				className="grid p-fluid"
				style={{ paddingLeft: "20px", paddingRight: "20px" }}
			>
				<div className="col-12 md:col-4">
					<Button
						label={t("formular.btn-submit-booking")}
						icon="pi pi-forward"
						onClick={() => submitForm()}
					/>
				</div>
			</div>
			<div
				className="grid p-fluid"
				style={{
					paddingLeft: "20px",
					paddingRight: "20px",
					marginBottom: "15px",
				}}
			>
				<div className="col-12 md:col-4">
					{whitelabel === "1" ? (
						<div style={{ marginTop: "10px" }}>
							<span style={{ fontSize: "0.7rem" }}>powerd by DrivePartner</span>
						</div>
					) : null}
					<div style={{ paddingBottom: "100px" }} />
				</div>
			</div>
			<Footer userData={userData} />
		</>
	);
};

export default FixPriceRoutesScreen;
